import { observer } from 'mobx-react-lite'
import { Box, Drawer, ListItemIcon, MenuItem, Pagination, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useMyTeamStore } from '../../store/my-team.store'
import { TeamRolesUsersListCard } from './TeamRolesUsersListCard'
import { AppMenu } from 'src/component/common/analog/molecule/list/AppMenu'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CheckIcon from '@mui/icons-material/Check'
import { SkillsSearchBar } from './SkillsSearchBar'
import { RoleOrganisationOverviewOrder, RoleOrganisationOverviewSortBy, TeamRolesUsersSkillGapMetrics } from '../../model/my-team-roles'
import { AdministratorUserDetailsPage } from 'src/feature/dashboard/page/AdministratorUserDetailsPage'

const pageSize = 5

type FilterOption<T> = { value: T; label: string }

export type TeamRolesUsersListProps = {
  levelId: string
  onAddUser: () => void
}

export const TeamRolesUsersList = observer(({ levelId, onAddUser }: TeamRolesUsersListProps) => {
  const { translation } = useLocalizationStore()
  const myTeamStore = useMyTeamStore()
  const { isLoading } = myTeamStore.getGenericFieldsState('getTeamRolesUsersSkillGapMetrics')

  const translations = translation.pages.myTeam
  const { rolesTable } = translations
  const { userFilter } = translation.pages.controlpanelOrganisation
  const [skip, setSkip] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [showMenu, setShowMenu] = useState(false)
  const [sortBy, setSortBy] = useState<RoleOrganisationOverviewSortBy>('alphabetical')
  const [order, setOrder] = useState<RoleOrganisationOverviewOrder>('a-z')
  const [selectedUserMeta, setSelectedUserMeta] = useState<TeamRolesUsersSkillGapMetrics | null>(null)
  const menuItemRef = useRef<HTMLButtonElement | null>(null)

  const sortOptions: FilterOption<RoleOrganisationOverviewSortBy>[] = useMemo(() => [
    { value: 'alphabetical', label: userFilter.sortAlphabetical() },
    { value: 'date-created', label: userFilter.sortDateCreated() },
  ], [userFilter])

  const orderOptions: FilterOption<RoleOrganisationOverviewOrder>[] = useMemo(() => (
    sortBy === 'alphabetical'
      ? [
        { value: 'a-z', label: userFilter.orderAlphabeticalAZ() },
        { value: 'z-a', label: userFilter.orderAlphabeticalZA() },
      ]
      : [
        { value: 'newest-first', label: userFilter.orderNewestFirst() },
        { value: 'oldest-first', label: userFilter.orderOldestFirst() },
      ]
  ), [sortBy, userFilter])

  const selectedSortLabel = useMemo(
    () => sortOptions.find(option => option.value === sortBy)?.label || sortOptions[0].label,
    [sortBy, sortOptions],
  )
  const selectedOrderLabel = useMemo(
    () => orderOptions.find(option => option.value === order)?.label || orderOptions[0].label,
    [order, orderOptions],
  )
  const menuLabel = useMemo(() => `${selectedSortLabel} - ${selectedOrderLabel}`, [selectedOrderLabel, selectedSortLabel])

  useEffect(() => {
    setSkip(0)
  }, [levelId])

  useEffect(() => {
    myTeamStore.getTeamRolesUsersSkillGapMetrics(levelId, skip, pageSize, searchText, sortBy, order)
  }, [levelId, myTeamStore, order, searchText, skip, sortBy])

  const usersList = myTeamStore.teamRolesUsersSkillGapMetricsList
  const users = usersList.users ?? []
  const hasUsers = users.length > 0
  const totalUsers = usersList.totalUsers ?? 0
  const shouldShowEmptyState = !isLoading && !hasUsers
  const shouldShowUsersState = !isLoading && hasUsers
  const filteredUsersCount = usersList.filteredUsers ?? usersList.totalUsers ?? users.length
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredUsersCount / pageSize)), [filteredUsersCount])
  const currentPage = Math.floor(skip / pageSize) + 1
  const visibleStart = hasUsers ? skip + 1 : 0
  const visibleEnd = hasUsers ? Math.min(skip + users.length, filteredUsersCount) : 0
  const isDetailsDrawerOpen = selectedUserMeta !== null
  const resultsSummary = translations.resultsSummary(visibleStart, visibleEnd, filteredUsersCount)
  const totalUsersSummary = `${translations.metricsHeader.totalUsers()}: ${totalUsers}`

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    setSkip((page - 1) * pageSize)
  }, [])

  const handleSearchChange = useCallback((searchValue: string) => {
    setSearchText(searchValue)
    setSkip(0)
  }, [])

  const handleToggleMenu = useCallback(() => {
    setShowMenu(previous => !previous)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setShowMenu(false)
  }, [])

  const handleSortByChange = useCallback((option: RoleOrganisationOverviewSortBy) => {
    setSortBy(option)
    setOrder(option === 'alphabetical' ? 'a-z' : 'newest-first')
    setSkip(0)
    setShowMenu(false)
  }, [])

  const handleOrderChange = useCallback((option: RoleOrganisationOverviewOrder) => {
    setOrder(option)
    setSkip(0)
    setShowMenu(false)
  }, [])

  const handleOpenUserDetails = useCallback((user: TeamRolesUsersSkillGapMetrics) => {
    setSelectedUserMeta(user)
  }, [])

  const handleCloseUserDetails = useCallback(() => {
    setSelectedUserMeta(null)
  }, [])

  return (
    <Box sx={styles.cardWrapper}>
      <AppBox sx={styles.searchBoxContainer}>
        <AppBox sx={styles.searchBox}>
          <SkillsSearchBar
            count={usersList.filteredUsers ?? 0}
            value={searchText}
            onDebouncedChange={handleSearchChange}
            debounceDelay={600}
          />
        </AppBox>
        <AppBox sx={styles.actionsRow}>
          <AppBox
            component='button'
            type='button'
            sx={styles.sortTrigger}
            ref={menuItemRef}
            onClick={handleToggleMenu}
            aria-controls={showMenu ? 'roles-users-sort-menu' : undefined}
            aria-expanded={showMenu ? 'true' : undefined}
            aria-haspopup='menu'
          >
            {menuLabel}
            {showMenu ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'inherit' }} /> : <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'inherit' }} />}
          </AppBox>
          <AppMenu
            anchorEl={menuItemRef.current}
            id="roles-users-sort-menu"
            open={showMenu}
            onClose={handleCloseMenu}
          >
            <Typography sx={styles.menuSectionTitle}>{userFilter.sortBy()}</Typography>
            {sortOptions.map(option => (
              <MenuItem key={option.value} onClick={() => handleSortByChange(option.value)} sx={styles.menuItem}>
                <ListItemIcon>{sortBy === option.value && <CheckIcon fontSize="small" sx={{ color: 'text.primary' }} />}</ListItemIcon>
                {option.label}
              </MenuItem>
            ))}
            <Typography sx={styles.menuSectionTitle}>{userFilter.order()}</Typography>
            {orderOptions.map(option => (
              <MenuItem key={option.value} onClick={() => handleOrderChange(option.value)} sx={styles.menuItem}>
                <ListItemIcon>{order === option.value && <CheckIcon fontSize="small" sx={{ color: 'text.primary' }} />}</ListItemIcon>
                {option.label}
              </MenuItem>
            ))}
          </AppMenu>
        </AppBox>
      </AppBox>

      {shouldShowEmptyState && (
        <AppBox sx={styles.emptyStateContainer}>
          <Typography sx={styles.emptyStateText}>
            {translations.noUsersMsg()}{' '}
            <Typography component='span' sx={styles.routeLink} onClick={onAddUser}>
              {rolesTable.roleUsersTodoAction()}
            </Typography>
          </Typography>
        </AppBox>
      )}

      {shouldShowUsersState && <>
        <AppBox sx={styles.cardContainer}>
          {users.map(user => (
            <TeamRolesUsersListCard key={user.id} user={user} isLoading={isLoading} onOpenDetails={handleOpenUserDetails} />
          ))}
        </AppBox>

        <AppBox sx={styles.paginationRow}>
          <Typography sx={styles.paginationSummary}>{resultsSummary}</Typography>
          <Typography sx={styles.paginationSummary}>{totalUsersSummary}</Typography>
          <Pagination
            variant='outlined'
            shape='rounded'
            color='secondary'
            count={totalPages}
            page={Math.min(currentPage, totalPages)}
            showFirstButton
            showLastButton
            onChange={handlePageChange}
          />
        </AppBox>
      </>}

      {isLoading && <TeamRolesUsersListCard isLoading={true} loadingCount={3} />}

      <Drawer
        anchor='right'
        open={isDetailsDrawerOpen}
        onClose={handleCloseUserDetails}
        ModalProps={{ keepMounted: true }}
        sx={styles.detailsDrawer}
        PaperProps={{ sx: styles.detailsDrawerPaper }}
        BackdropProps={{ sx: styles.detailsDrawerBackdrop }}
      >
        {selectedUserMeta && (
          <AdministratorUserDetailsPage
            isSidePanel={true}
            onClosePanel={handleCloseUserDetails}
            initialUserMeta={{
              userId: selectedUserMeta.id,
              name: selectedUserMeta.name ?? '',
              email: selectedUserMeta.email ?? '',
              avatarUrl: selectedUserMeta.avatarUrl ?? '',
              memberSince: selectedUserMeta.createdAt ?? '',
            }}
          />
        )}
      </Drawer>
    </Box>
  )
})

const styles = {
  cardWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  searchBoxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    width: '100%',
    flexShrink: 0,
  },
  searchBox: {
    flex: 1,
    minWidth: 0,
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  sortTrigger: {
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
    backgroundColor: 'primary.main',
    color: 'background.default',
    p: '5px 10px',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: 'primary.main',
  },
  menuSectionTitle: {
    color: 'text.secondary',
    fontSize: 11,
    p: '4px 10px',
  },
  menuItem: {
    fontSize: 12,
    '& .MuiListItemIcon-root': {
      minWidth: 24,
    },
  },
  cardContainer: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: { xs: 'center', md: 'flex-start' },
    gap: 10,
  },
  paginationRow: {
    width: '100%',
    flexShrink: 0,
    display: 'flex',
    justifyContent: { xs: 'center', md: 'space-between' },
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    backgroundColor: 'background.dark',
    padding: '10px 20px',
    border: '1px solid',
    borderColor: 'background.default',
    borderRadius: '10px',
  },
  paginationSummary: {
    color: 'text.secondary',
    fontSize: 12,
    width: { xs: '100%', md: 'auto' },
    textAlign: { xs: 'center', md: 'left' },
  },
  emptyStateContainer: {
    color: 'text.secondary',
  },
  emptyStateText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  routeLink: {
    color: 'secondary.main',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  detailsDrawer: {
    zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 2,
  },
  detailsDrawerBackdrop: {
    zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 1,
    backgroundColor: 'rgba(6, 17, 28, 0.42)',
  },
  detailsDrawerPaper: {
    width: { xs: '100%', sm: 'min(92vw, 720px)' },
    maxWidth: '100%',
    backgroundColor: 'background.dark',
    overflowY: 'scroll',
    scrollbarGutter: 'stable',
    zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 2,
  },
}
